import {
    Checkbox,
    FormLabel,
    IdOptionType,
    InputList,
    OptionType,
    Radiogroup,
    RadiogroupOptions,
    SearchableSelect,
    Textinput
} from "shared";
import {
    ITemplateComponent,
    ITemplateRadioComponent,
    radioDirectionTypes
} from "entities/template";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { VisibilityField } from "../../form_fields/visibility_field/visibility_field";
import { LabelField } from "../../form_fields/label_field/label_field";
import { TooltipField } from "../../form_fields/tooltip_field/tooltip_field";
import styles from "./component_form_radio.module.scss";
import { useComponentValidateFunction } from "../../../../lib/hooks/useComponentValidateFunction";

interface IComponentFormRadioProps {
    component: ITemplateRadioComponent;
    enLanguageEnabled: boolean;
    componentList: ITemplateComponent[];
    onChange: (component: ITemplateComponent) => void;
    triggerValidate: number;
}

export const ComponentFormRadio: React.FC<IComponentFormRadioProps> = React.memo(({
                                                                                      component,
                                                                                      enLanguageEnabled,
                                                                                      componentList,
                                                                                      triggerValidate,
                                                                                      onChange
                                                                                  }) => {
    const t = useComponentsTranslations();
    const { validateTag, validateList } = useComponentValidateFunction();
    const { i18n } = useTranslation();

    const [defaultValueOptions, setDefaultValueOptions] = useState<IdOptionType[]>([]);

    const optionMap = (value: string, id: number) => {
        return {
            value,
            label: value,
            id
        }
    };

    useEffect(() => {
        Array.isArray(component.list) && setDefaultValueOptions(component.list.map(optionMap));
    }, [component.list])


    const onInputListChange = (list: string[], list_en: string[]) => {
        let defaultValue: string | undefined = undefined;

        if (component.defaultValue && list.includes(component.defaultValue)) {
            defaultValue = component.defaultValue;
        }

        onChange({
            ...component,
            list,
            list_en,
            defaultValue
        });
    };


    const directionOptions: RadiogroupOptions[] = useMemo(() => {
        const options = [{
            label: t("row"),
            value: radioDirectionTypes.row
        }, {
            label: t("col"),
            value: radioDirectionTypes.col
        }];

        return options;
    }, [i18n.language]);


    const onFieldChange = (value: Partial<ITemplateRadioComponent>) => {
        onChange({
            ...component,
            ...value
        })
    };


    return <div className={styles.container}>
        <LabelField component={component}
                    enLanguageEnabled={enLanguageEnabled}
                    title={t("formLabel")}
                    name="label-dynamic-input"
                    onChange={onFieldChange}/>

        <FormLabel title={t("formTag")} required={true}>
            <Textinput size="m"
                       triggerValidate={triggerValidate}
                       validate={true}
                       validateFunc={validateTag}
                       value={component.name ? component.name : ""}
                       onChange={(e) => onFieldChange({ name: e.target.value })}
                       view="outline"/>
        </FormLabel>

        <FormLabel title={t("list")}>
            <InputList
                listId={component.id}
                list={component.list}
                list_en={component.list_en}
                triggerValidate={triggerValidate}
                validate={true}
                validateFunc={validateList}
                enLanguageEnabled={enLanguageEnabled}
                onChange={options => onInputListChange(
                    options.map((o) => o.value),
                    options.map((o) => o.value_en)
                )
                }
            />
        </FormLabel>

        <FormLabel title={t("defaultValue")}>
            <SearchableSelect options={defaultValueOptions}
                              optionValue={defaultValueOptions.find((o) => o.value === component.defaultValue) as OptionType}
                              onChange={(value) => onFieldChange({ defaultValue: value as string })}
                              isMulti={false}
                              hideClearIcon={false}
                              isSearchable={true}/>
        </FormLabel>

        <VisibilityField
            component={component}
            componentList={componentList}
            onChange={onFieldChange}/>

        <FormLabel title={t("arrangement")}>
            <Radiogroup vertical={false}
                        name="direction-radio"
                        options={directionOptions}
                        onChange={(value) => onFieldChange({
                            direction: value === "undefined" || value === radioDirectionTypes.row ? undefined : value as radioDirectionTypes
                        })}
                        value={component.direction ? component.direction : radioDirectionTypes.row}
            />
        </FormLabel>

        <TooltipField
            component={component}
            enLanguageEnabled={enLanguageEnabled}
            onChange={onFieldChange}
        />

        <div className={styles.checkboxGroup}>
            <Checkbox
                label={t("requiredField")}
                size="m"
                onChange={() => onFieldChange({ required: typeof component.required === "boolean" ? !component.required : false })}
                checked={typeof component.required === "boolean" ? component.required : true}
            />
            <Checkbox
                label={t("rowGroup")}
                size="m"
                onChange={() => onFieldChange({ isRowGroup: !component.isRowGroup })}
                checked={component.isRowGroup}
            />
        </div>

        <div className={styles.checkboxGroup}>
            <Checkbox
                label={t("readonlyField")}
                size="m"
                onChange={() => onFieldChange({ readonly: !component.readonly })}
                checked={component.readonly}
            />
        </div>

    </div>;
});

